import React, { useState, useEffect } from 'react';
import api from '../../api';

function ServicesAndBranchesPanel({ companyId, onDragStart }) {
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchCompanyData() {
      if (!companyId) {
        setCompanyData(null);
        return;
      }

      setLoading(true);
      try {
        // Fetch company details
        const companyResponse = await api.get(`/companies/${companyId}`);
        const company = companyResponse.data;

        // Fetch company-wide services (tasks with branchId = null)
        const tasksResponse = await api.get(`/tasks/company/${companyId}`);
        console.log('Fetched tasks:', tasksResponse.data); // Debug log
        const companyServices = tasksResponse.data
          .filter(task => task.branchId === null && (task.subtype || task.type)) // Company-wide tasks only
          .map(task => ({
            id: task.id,
            name: task.subtype || task.type, // Use subtype if available, fallback to type
            type: task.type,
            companyId: task.companyId
          }));

        console.log('Extracted services:', companyServices); // Debug log

        // Add services to company data
        company.services = companyServices;

        setCompanyData(company);
      } catch (err) {
        console.error('Error fetching company data:', err);
        setCompanyData(null);
      } finally {
        setLoading(false);
      }
    }

    fetchCompanyData();
  }, [companyId]);

  const handleDragStart = (e, item, type) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({ ...item, itemType: type }));
    
    // Visual feedback
    e.target.style.opacity = '0.5';
    
    if (onDragStart) {
      onDragStart(item, type);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
  };

  if (!companyId) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <p className="text-sm font-medium">Select a company to view services and branches</p>
        <p className="text-xs mt-1">You'll be able to drag items onto the timeline</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading company data...</p>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className="p-6 text-center text-red-500">
        <p className="text-sm">Failed to load company data</p>
      </div>
    );
  }

  // Get services array (already parsed in useEffect)
  const services = companyData.services || [];

  const branches = companyData.Branches || [];

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <div className="p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg">
        <h3 className="font-bold text-lg">{companyData.name}</h3>
        <p className="text-sm opacity-90 mt-1">{companyData.contactPerson}</p>
        <div className="flex items-center gap-4 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            {branches.length} Branches
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            {services.length} Services
          </span>
        </div>
      </div>

      {/* Services Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">SERVICES</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">(Drag to timeline)</span>
        </div>
        
        {services.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic p-3 bg-gray-50 dark:bg-gray-800 rounded">
            No services configured for this company
          </p>
        ) : (
          <div className="space-y-2">
            {services.map((service) => (
              <div
                key={service.id}
                draggable
                onDragStart={(e) => handleDragStart(e, { id: service.id, name: service.name, companyId }, 'service')}
                onDragEnd={handleDragEnd}
                className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg cursor-move hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-400 dark:hover:border-blue-600 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.name}</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Branches Section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
          <h4 className="text-sm font-bold text-gray-900 dark:text-white">BRANCHES</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">(Drag to timeline)</span>
        </div>
        
        {branches.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic p-3 bg-gray-50 dark:bg-gray-800 rounded">
            No branches configured for this company
          </p>
        ) : (
          <div className="space-y-2">
            {branches.map((branch) => (
              <div
                key={branch.id}
                draggable
                onDragStart={(e) => handleDragStart(e, { ...branch, companyId }, 'branch')}
                onDragEnd={handleDragEnd}
                className="px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-lg cursor-move hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:border-purple-400 dark:hover:border-purple-600 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{branch.name}</div>
                      {branch.address && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{branch.address}</div>
                      )}
                      {/* Show progress if available */}
                      {branch.setupProgress !== undefined && branch.setupProgress !== null && (
                        <div className="mt-1 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-600 transition-all"
                              style={{ width: `${branch.setupProgress}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {branch.setupProgress}%
                          </span>
                        </div>
                      )}
                      {branch.status && (
                        <div className="mt-1 inline-block">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            branch.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            branch.status === 'Pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {branch.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          <span className="font-semibold">💡 Tip:</span> Drag services and branches onto the Gantt chart to create scheduled tasks. 
          Stretch the bars to set duration, and right-click to assign resources.
        </p>
      </div>
    </div>
  );
}

export default ServicesAndBranchesPanel;

